/**
 * Vue maire consolidée — niveau 1 (pilotage agrégé tous services).
 *
 * Réservée admin / super_admin. Affiche :
 *  - un bandeau « points d'attention » transverses (ce qui demande une action) ;
 *  - une grille de cartes par module activé, chacune avec ses KPIs + un bouton
 *    « Détail » (niveau 2, à venir) ;
 *  - un sélecteur de modules (l'admin choisit ce qui s'affiche).
 *
 * Direction artistique RORONOA : dark first, glassmorphism, accent par module,
 * tokens OKLCH `var(--c-<module>)`. Aucune logique métier ici : lecture seule.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModules } from '@/lib/modules-context';
import { AppHeader } from '@/components/AppHeader';
import { Card, LogoMark, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api } from '@/lib/api';
import type {
  DashboardConsolide,
  DashboardModuleCard,
  DashboardAttentionPoint,
} from '@/lib/api';
import {
  Hammer,
  MessageSquare,
  GraduationCap,
  Users,
  CalendarDays,
  Clock,
  ArrowRight,
  Calculator,
  Baby,
  FileText,
  SlidersHorizontal,
  AlertTriangle,
  Check,
} from 'lucide-react';

// Map module → accent CSS (clé du design system). Salles = tech (bleu).
const MODULE_ACCENT: Record<string, string> = {
  tech: 'tech',
  grc: 'grc',
  ecole: 'cantine',
  asso: 'asso',
  salle: 'tech',
  rh: 'rh',
  compta: 'compta',
  petite_enfance: 'petite_enfance',
  urba: 'urba',
};

const MODULE_ICON: Record<string, typeof Hammer> = {
  tech: Hammer,
  grc: MessageSquare,
  ecole: GraduationCap,
  asso: Users,
  salle: CalendarDays,
  rh: Clock,
  compta: Calculator,
  petite_enfance: Baby,
  urba: FileText,
};

// Libellés du sélecteur (ordre + sous-titre)
const MODULE_META: { key: string; label: string; sub: string }[] = [
  { key: 'tech', label: 'Service technique', sub: 'signalements · tournées' },
  { key: 'grc', label: 'Relation citoyen (GRC)', sub: 'demandes · alertes' },
  { key: 'ecole', label: 'Scolaire & cantine', sub: 'repas · familles' },
  { key: 'asso', label: 'Vie associative', sub: 'annuaire · subventions' },
  { key: 'salle', label: 'Salles & matériel', sub: 'réservations · service technique' },
  { key: 'rh', label: 'Ressources humaines', sub: 'présence · absences' },
  { key: 'compta', label: 'Comptabilité', sub: 'encaissements · dépôts' },
  { key: 'petite_enfance', label: 'Petite enfance', sub: 'crèche · contrats' },
  { key: 'urba', label: 'Urbanisme', sub: 'dossiers · arrêtés' },
];

// Correspondance clé-vue-maire → clé service (tenant_modules). Les clés sans
// correspondance (tech/grc/salle/rh) sont du socle ou transverses → toujours visibles.
const VUE_KEY_TO_SERVICE: Record<string, string> = {
  ecole: 'scolaire_cantine',
  asso: 'associations',
  urba: 'urbanisme',
  compta: 'comptable',
  petite_enfance: 'petite_enfance',
};

function toneColor(tone?: string): string | undefined {
  switch (tone) {
    case 'good':
      return 'oklch(0.88 0.16 155)';
    case 'warn':
      return 'oklch(0.92 0.12 75)';
    case 'danger':
      return 'oklch(0.94 0.12 25)';
    case 'info':
      return 'oklch(0.88 0.10 230)';
    default:
      return undefined;
  }
}

function attnStyle(tone: string): { bg: string; border: string; fg: string } {
  switch (tone) {
    case 'danger':
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.14 25 / 0.20), oklch(var(--c-bg) / 0.4))',
        border: 'oklch(0.68 0.22 25 / 0.32)',
        fg: 'oklch(0.92 0.10 25)',
      };
    case 'info':
      return {
        bg: 'linear-gradient(180deg, oklch(0.28 0.12 230 / 0.18), oklch(var(--c-bg) / 0.4))',
        border: 'oklch(0.70 0.15 230 / 0.30)',
        fg: 'oklch(0.88 0.09 230)',
      };
    default:
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.12 75 / 0.18), oklch(var(--c-bg) / 0.4))',
        border: 'oklch(0.80 0.17 75 / 0.28)',
        fg: 'oklch(0.90 0.09 75)',
      };
  }
}

export default function VueMaireConsolidee() {
  const navigate = useNavigate();
  const { isModuleActif } = useModules();
  const [data, setData] = useState<DashboardConsolide | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sélecteur de modules
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [savingPrefs, setSavingPrefs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.dashboard.consolide();
      setData(result);
    } catch {
      setError('Impossible de charger le tableau de bord. Réessayez.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openSelector = useCallback(async () => {
    try {
      const r = await api.dashboard.getModules();
      setPrefs(r.modules);
      setSelectorOpen(true);
    } catch {
      setError('Impossible de charger les préférences de modules.');
    }
  }, []);

  const saveSelector = useCallback(async () => {
    setSavingPrefs(true);
    try {
      await api.dashboard.setModules(prefs);
      setSelectorOpen(false);
      await load();
    } catch {
      setError('Échec de l\'enregistrement des modules.');
    } finally {
      setSavingPrefs(false);
    }
  }, [prefs, load]);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Pilotage" accent="tech" />

      <main className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        {/* En-tête */}
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div className="flex items-center gap-3">
            <LogoMark icon={FileText} accent="tech" size="md" />
            <div>
              <SectionEyebrow>
                {data?.commune ?? 'Commune'} ·{' '}
                {data
                  ? new Date(data.generatedAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                    })
                  : '—'}
              </SectionEyebrow>
              <h1 className="font-display text-lg font-semibold tracking-r-snug text-ink">
                Tableau de bord du maire
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openSelector}
              className="inline-flex h-9 items-center gap-2 rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink-muted backdrop-blur-glass transition-colors hover:border-tech/40 hover:bg-tech/10"
            >
              <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
              Personnaliser
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard/maire/export')}
              className="inline-flex h-9 items-center gap-2 rounded-r-md bg-gradient-tech px-3 text-sm font-medium text-white shadow-glow-tech transition-transform hover:scale-[1.02]"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Export conseil
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-r-md border border-urgent/40 bg-urgent/10 px-4 py-3 text-sm text-ink">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" aria-label="Chargement" />
          </div>
        )}

        {!loading && data && (
          <>
            {/* Points d'attention */}
            {data.attention.length > 0 && (
              <>
                <div className="mb-2 text-xs uppercase tracking-wide text-ink-subtle">
                  Points d'attention
                </div>
                <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {data.attention.map((p: DashboardAttentionPoint) => {
                    const s = attnStyle(p.tone);
                    return (
                      <div
                        key={p.key}
                        className="rounded-r-md border p-3 backdrop-blur-glass"
                        style={{ background: s.bg, borderColor: s.border }}
                      >
                        <div className="text-2xl font-semibold" style={{ color: s.fg }}>
                          {p.value}
                        </div>
                        <div className="mt-0.5 text-xs" style={{ color: s.fg }}>
                          {p.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Cartes par service */}
            <div className="mb-2 text-xs uppercase tracking-wide text-ink-subtle">
              Par service activé
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.modules.filter((m: DashboardModuleCard) => isModuleActif(VUE_KEY_TO_SERVICE[m.key])).map((m: DashboardModuleCard) => {
                const accent = MODULE_ACCENT[m.key] ?? 'tech';
                const Icon = MODULE_ICON[m.key] ?? Hammer;
                return (
                  <Card key={m.key} variant="glass" className="overflow-hidden p-0">
                    <div
                      className="h-[3px] w-full"
                      style={{
                        background: `linear-gradient(90deg, oklch(var(--c-${accent})), transparent)`,
                      }}
                    />
                    <div className="p-4">
                      <div className="mb-3 flex items-center gap-2.5">
                        <span
                          className="flex h-8 w-8 items-center justify-center rounded-r-md"
                          style={{
                            background: `oklch(var(--c-${accent}) / 0.16)`,
                            border: `1px solid oklch(var(--c-${accent}) / 0.3)`,
                          }}
                        >
                          <Icon
                            className="h-4 w-4"
                            style={{ color: `oklch(var(--c-${accent}-300))` }}
                            aria-hidden="true"
                          />
                        </span>
                        <div className="font-display text-sm font-semibold text-ink">
                          {m.label}
                        </div>
                      </div>
                      <dl className="space-y-1">
                        {m.kpis.map((k, i) => (
                          <div
                            key={i}
                            className="flex items-baseline justify-between border-t border-border/40 py-1 text-sm first:border-t-0"
                          >
                            <dt className="text-ink-muted">{k.label}</dt>
                            <dd
                              className="font-semibold tabular-nums"
                              style={{ color: toneColor(k.tone) }}
                            >
                              {k.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/maire/${m.key}`)}
                        className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-r-md border border-border bg-surface/40 px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-tech/40 hover:bg-tech/10"
                      >
                        Détail
                        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>

            <p className="mt-5 text-center text-xs text-ink-subtle">
              Les modules désactivés chez la commune n'apparaissent pas · données temps réel
            </p>
          </>
        )}
      </main>

      {/* Sélecteur de modules */}
      <Dialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        title="Modules affichés sur le tableau de bord"
      >
        <p className="mb-4 text-sm text-ink-muted">
          Choisissez les services visibles dans la vue consolidée. Un module masqué reste
          accessible, il n'apparaît simplement pas ici.
        </p>
        <div className="space-y-2">
          {MODULE_META.filter((mod) => isModuleActif(VUE_KEY_TO_SERVICE[mod.key])).map((mod) => {
            const accent = MODULE_ACCENT[mod.key] ?? 'tech';
            const Icon = MODULE_ICON[mod.key] ?? Hammer;
            const on = prefs[mod.key] !== false;
            return (
              <div
                key={mod.key}
                className="flex items-center gap-3 rounded-r-md border border-border bg-surface/40 p-3"
              >
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-r-md"
                  style={{
                    background: `oklch(var(--c-${accent}) / 0.16)`,
                    border: `1px solid oklch(var(--c-${accent}) / 0.3)`,
                  }}
                >
                  <Icon
                    className="h-4 w-4"
                    style={{ color: `oklch(var(--c-${accent}-300))` }}
                    aria-hidden="true"
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">{mod.label}</div>
                  <div className="text-xs text-ink-subtle">{mod.sub}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${on ? 'Masquer' : 'Afficher'} ${mod.label}`}
                  onClick={() => setPrefs((p) => ({ ...p, [mod.key]: !on }))}
                  className="relative h-6 w-11 flex-shrink-0 rounded-full border border-border transition-colors"
                  style={{
                    background: on ? 'oklch(0.72 0.18 155)' : 'oklch(var(--c-surface))',
                    borderColor: on ? 'transparent' : undefined,
                  }}
                >
                  <span
                    className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all"
                    style={{ left: on ? '22px' : '2px' }}
                  />
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setSelectorOpen(false)}
            className="rounded-r-md border border-border px-4 py-2 text-sm text-ink-muted hover:bg-surface/60"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={saveSelector}
            disabled={savingPrefs}
            className="inline-flex items-center gap-2 rounded-r-md bg-gradient-tech px-4 py-2 text-sm font-medium text-white shadow-glow-tech disabled:opacity-60"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            {savingPrefs ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </Dialog>
    </div>
  );
}
